"use client"
import React, { useEffect, useRef, useState } from "react";
import {Navbar, NavbarBrand, NavbarContent, Link, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button, NavbarItem} from "@nextui-org/react";
import Image from "next/image";
import {usePathname } from 'next/navigation'
import {urls} from "@/lib/urls";
import {isPathCorrectCategory} from "@/lib/utils";
import { ChevronDown, Menu, Moon, Sun, X } from "lucide-react";
import { useTheme } from "@/app/ThemeProvider";
import { ToolIcon } from "@/lib/toolIcons";
import QuickSwitch from "@/components/QuickSwitch";

export default function NavbarComponent() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const pathname = usePathname();
    const { theme, toggleTheme } = useTheme();
    const menuButtonRef = useRef<HTMLButtonElement>(null);

    // Close the mobile menu whenever the route changes
    useEffect(() => {
        setIsMenuOpen(false);
    }, [pathname]);

    // Escape to close, and lock body scroll while the menu is open
    useEffect(() => {
        if (!isMenuOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setIsMenuOpen(false);
                menuButtonRef.current?.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [isMenuOpen]);

    return(
        <Navbar
            className={"border-b-1 sticky top-0 z-50"}
            aria-label="Main navigation"
        >
            <NavbarContent>
                <Button
                    ref={menuButtonRef}
                    isIconOnly
                    variant="light"
                    radius="full"
                    className="sm:hidden"
                    aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                    aria-expanded={isMenuOpen}
                    aria-controls="mobile-menu-panel"
                    onClick={() => setIsMenuOpen((open) => !open)}
                >
                    {isMenuOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
                </Button>
                <NavbarBrand>
                    <Link href={"/"} aria-label="FormatChamp Home">
                        <Image
                            src={"/logo-transparent.svg"}
                            alt={"Format Champ - Free Online Code Formatter and Validator Tools"}
                            width={45}
                            height={15}
                            priority
                        />
                        <p className="ml-2 font-bold text-inherit" style={{color: "#004AAD"}}>FormatChamp</p>
                    </Link>
                </NavbarBrand>
            </NavbarContent>

            <NavbarContent className="hidden sm:flex gap-4" justify="center">
                {urls.map((item) => (
                    item.subGroups ? (
                        <Dropdown key={item.path}>
                            <NavbarItem>
                                <DropdownTrigger>
                                    <Button
                                        disableRipple
                                        className="p-0 bg-transparent data-[hover=true]:bg-transparent"
                                        radius="sm"
                                        variant="light"
                                        endContent={<ChevronDown className="w-4 h-4" aria-hidden="true" />}
                                    >
                                        {item.category.label}
                                    </Button>
                                </DropdownTrigger>
                            </NavbarItem>
                            <DropdownMenu
                                aria-label={`${item.category.label} options`}
                                className="w-[200px]"
                                itemClasses={{
                                    base: "gap-4",
                                }}
                                onAction={(key) => {
                                    const path = key.toString();
                                    window.location.href = path;
                                }}
                            >
                                {item.subGroups.map((subItem) => (
                                    <DropdownItem
                                        key={subItem.path}
                                        description={subItem.footerLabel}
                                        startContent={<ToolIcon value={subItem.value} className="w-4 h-4" />}
                                        color={isPathCorrectCategory(pathname, subItem.path) ? "primary" : "default"}
                                        aria-current={isPathCorrectCategory(pathname, subItem.path) ? "page" : undefined}
                                    >
                                        {subItem.label}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </Dropdown>
                    ) : (
                        <NavbarItem key={item.path}>
                            <Link
                                href={item.path}
                                className="text-foreground flex items-center gap-1.5"
                                color={isPathCorrectCategory(pathname, item.path) ? "primary" : "foreground"}
                                aria-current={isPathCorrectCategory(pathname, item.path) ? "page" : undefined}
                            >
                                <ToolIcon value={item.category.value} className="w-4 h-4" />
                                {item.category.label}
                            </Link>
                        </NavbarItem>
                    )
                ))}
            </NavbarContent>

            <NavbarContent justify="end" className="gap-2">
                <NavbarItem>
                    <QuickSwitch />
                </NavbarItem>
                <NavbarItem>
                    <Button
                        isIconOnly
                        variant="light"
                        radius="full"
                        aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
                        onClick={toggleTheme}
                    >
                        {theme === "dark" ? (
                            <Sun className="w-5 h-5" aria-hidden="true" />
                        ) : (
                            <Moon className="w-5 h-5" aria-hidden="true" />
                        )}
                    </Button>
                </NavbarItem>
            </NavbarContent>

            {/* Custom mobile menu - deliberately not NextUI's NavbarMenu/NavbarMenuToggle */}
            {isMenuOpen && (
                <>
                    <div
                        className="sm:hidden fixed inset-x-0 bottom-0 top-16 z-40 bg-black/30 dark:bg-black/50"
                        onClick={() => setIsMenuOpen(false)}
                        aria-hidden="true"
                    />
                    <div
                        id="mobile-menu-panel"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Mobile navigation menu"
                        className="sm:hidden fixed inset-x-0 top-16 z-40 max-h-[calc(100vh-4rem)] overflow-y-auto bg-white dark:bg-slate-900 border-b-1 border-gray-200 dark:border-gray-700 shadow-lg"
                    >
                        <nav className="px-4 py-4 flex flex-col gap-5">
                            {urls.map((item) => (
                                <div key={item.path}>
                                    {item.subGroups ? (
                                        <>
                                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                                                {item.category.label}
                                            </p>
                                            <div className="flex flex-col gap-1">
                                                {item.subGroups.map((subItem) => {
                                                    const active = isPathCorrectCategory(pathname, subItem.path);
                                                    return (
                                                        <Link
                                                            key={subItem.path}
                                                            href={subItem.path}
                                                            className={
                                                                "flex items-center gap-3 py-2.5 px-2 rounded-lg " +
                                                                (active
                                                                    ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300"
                                                                    : "text-gray-700 dark:text-gray-200")
                                                            }
                                                            aria-current={active ? "page" : undefined}
                                                        >
                                                            <ToolIcon value={subItem.value} className="w-4 h-4 shrink-0" />
                                                            {subItem.label}
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    ) : (
                                        <Link
                                            href={item.path}
                                            className={
                                                "flex items-center gap-3 py-2.5 px-2 rounded-lg font-medium " +
                                                (isPathCorrectCategory(pathname, item.path)
                                                    ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300"
                                                    : "text-gray-700 dark:text-gray-200")
                                            }
                                            aria-current={isPathCorrectCategory(pathname, item.path) ? "page" : undefined}
                                        >
                                            <ToolIcon value={item.category.value} className="w-4 h-4 shrink-0" />
                                            {item.category.label}
                                        </Link>
                                    )}
                                </div>
                            ))}
                        </nav>
                    </div>
                </>
            )}
        </Navbar>
    )
}
